/**
 * Text/image block video (FIL001-17) — a moving picture in the media box: muted, looping,
 * no controls, cropped like the still it stands in for. The reasoning is the hero's, and
 * this element is deliberately the same as `filliers-hero-video` (`filliers-hero.js`):
 * play() lives here instead of an `autoplay` attribute because a CSS media query cannot
 * stop a video, and with `autoplay` in the markup a reduced-motion visitor would see
 * frames before this deferred script could withhold them.
 *
 * Kept as its own element rather than loading the hero's, so the live home hero is not
 * touched for this and neither block's markup names the other. If a third section wants a
 * background video, fold both into one shared element instead of copying this again.
 *
 * One difference with the hero worth knowing: a hero is above the fold, this block often
 * is not, and it starts playing — so downloading — on page load wherever it sits. Fine for
 * a short clip; not gated on the viewport, because IntersectionObserver proved unreliable
 * in preview contexts (see `filliers-visual.js`) and a scroll listener is more machinery
 * than a few-second loop deserves.
 */
class FilliersTextImageVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // The stylesheet already hides the element under this query, uncovering the still.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Belt and braces over the Liquid attributes, whatever `video_tag` chooses to emit.
    this.video.muted = true;
    this.video.playsInline = true;

    // A file that will not decode never rejects play() — it errors afterwards.
    this.video.addEventListener('error', () => this.fallBackToStill(), { once: true });

    const started = this.video.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => this.fallBackToStill());
    }
  }

  /** Autoplay refused or the file broke: drop the video out and uncover the still. */
  fallBackToStill() {
    this.dataset.state = 'failed';
  }
}

if (!customElements.get('filliers-text-image-video')) {
  customElements.define('filliers-text-image-video', FilliersTextImageVideo);
}
